/**
 * CenoX Business → Remittance & Hire Purchase.
 *
 * Three tabs over one register, in the order the work happens: the bikes
 * exist, riders settle weekly on them, and some are being bought. The owner
 * asked for two tabs and for a proper Bike Register underneath them — the
 * register is a tab rather than a settings screen because adding a bike and
 * assigning it is daily work, not configuration.
 *
 * Remittance is the index: it is the Monday-morning screen.
 *
 * Investors are not a tab here. Who they are and what they are owed live in
 * Finance → Investors, the one place their money is read from; the Bike
 * Register links each investor-owned bike to that profile.
 */

import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { cn } from '../../components/ui';
import { ScreenError } from '../../components/ScreenError';

const TABS = [
  { to: '/business/rider-finance', label: 'Rider Remittance', end: true },
  { to: '/business/rider-finance/hire-purchase', label: 'Hire Purchase' },
  { to: '/business/rider-finance/bikes', label: 'Bike Register' },
  { to: '/business/rider-finance/riders', label: 'Riders' },
] as const;

export function RiderFinanceLayout(): ReactNode {
  const location = useLocation();
  const current = TABS.find((t) => t.to === location.pathname)?.label ?? 'Remittance';

  return (
    <>
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-slate-900">Remittance &amp; Hire Purchase</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          What riders owe each week, and the bikes they are buying.
        </p>
      </header>

      <nav
        className="mb-5 flex gap-1 overflow-x-auto border-b border-slate-200 pb-px"
        aria-label="Remittance and hire purchase section"
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={'end' in tab ? tab.end : false}
            className={({ isActive }) =>
              cn(
                'whitespace-nowrap rounded-t-lg px-3.5 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-b-2 border-brand-600 text-brand-700'
                  : 'border-b-2 border-transparent text-slate-500 hover:text-slate-800',
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* Keyed on the path, so a failure on one tab does not follow the user
          to the next. Same reasoning as the Logistics strip. */}
      <ScreenError key={location.pathname} screen={current}>
        <Outlet />
      </ScreenError>
    </>
  );
}
